/**
 * Race, class, proficiency and instance facts from the WoW: Forever client
 * (`character-rules.json`, generated by `apps/api/scripts/gen-character-rules.ts`), and the
 * eligibility questions the optimizer and the build-data endpoints ask of them.
 */
import rules from './character-rules.json' with { type: 'json' };
import type { PlayerClass } from './classes.js';

export type Faction = 'Alliance' | 'Horde';
export type InstanceKind = 'dungeon' | 'raid' | 'battleground';

export interface CharacterRules {
  build: string;
  races: { id: number; name: string; faction: Faction; classes: PlayerClass[] }[];
  proficiencies: Record<string, { skill: string; minLevel: number; races: number[] | null }[]>;
  instances: Record<string, InstanceKind>;
  /**
   * Each class's talent trees in the talent window's left-to-right order: the tree icon's file
   * name and the `TalentTab.BackgroundFile` name of its background art ("PriestShadow").
   */
  talentTrees: Record<string, TalentTreeInfo[]>;
  /** Every profession in `PROFESSIONS` with its skill line's icon file name. */
  professions: { name: string; icon: string }[];
}

export interface TalentTreeInfo {
  name: string;
  icon: string;
  background: string;
}

export const CHARACTER_RULES = rules as CharacterRules;

/** The class's talent trees in the game's order (empty for an unknown class). */
export function talentTreesFor(cls: string): TalentTreeInfo[] {
  return CHARACTER_RULES.talentTrees[cls] ?? [];
}

/** Item `subclass` -> the equip skill line that governs it; absent means no skill is needed. */
export const SUBCLASS_SKILL: Record<string, string> = {
  Plate: 'Plate Mail',
  Mail: 'Mail',
  Leather: 'Leather',
  Cloth: 'Cloth',
  Shield: 'Shield',
  Dagger: 'Daggers',
  'One-Handed Sword': 'Swords',
  'Two-Handed Sword': 'Two-Handed Swords',
  'One-Handed Mace': 'Maces',
  'Two-Handed Mace': 'Two-Handed Maces',
  'One-Handed Axe': 'Axes',
  'Two-Handed Axe': 'Two-Handed Axes',
  Staff: 'Staves',
  Polearm: 'Polearms',
  Spears: 'Polearms',
  Bow: 'Bows',
  Gun: 'Guns',
  Crossbow: 'Crossbows',
  Wand: 'Wands',
  'Fist Weapon': 'Fist Weapons',
  Thrown: 'Thrown',
};
/** Relics only one class can equip (the client leaves their class mask open). */
export const RELIC_CLASS: Record<string, PlayerClass> = { Totem: 'Shaman', Idol: 'Druid', Libram: 'Paladin' };

const raceByName = new Map(CHARACTER_RULES.races.map((r) => [r.name, r]));

export function racesFor(faction: Faction): string[] {
  return CHARACTER_RULES.races.filter((r) => r.faction === faction).map((r) => r.name);
}

export function classesFor(raceName: string): PlayerClass[] {
  return raceByName.get(raceName)?.classes ?? [];
}

export function raceFaction(raceName: string): Faction | undefined {
  return raceByName.get(raceName)?.faction;
}

export function isValidCombination(faction: string, raceName: string, cls: string): boolean {
  const race = raceByName.get(raceName);
  return !!race && race.faction === faction && (race.classes as string[]).includes(cls);
}

/**
 * Whether a character of this class, race and level can wear or wield an item of the
 * subclass. Subclasses outside the table (Miscellaneous, Fishing Pole, ...) need no skill.
 */
export function canEquip(cls: string, raceName: string, subclass: string | undefined, level: number): boolean {
  if (!subclass) return true;
  const relicClass = RELIC_CLASS[subclass];
  if (relicClass) return cls === relicClass;
  const skill = SUBCLASS_SKILL[subclass];
  if (!skill) return true;
  const row = CHARACTER_RULES.proficiencies[cls]?.find((p) => p.skill === skill);
  if (!row) return false;
  if (level < row.minLevel) return false;
  if (row.races) {
    const race = raceByName.get(raceName);
    if (!race || !row.races.includes(race.id)) return false;
  }
  return true;
}

export function instanceKind(zoneName: string | undefined): InstanceKind | undefined {
  return zoneName ? CHARACTER_RULES.instances[zoneName] : undefined;
}
